import React, { useState } from 'react';
import { View, Text, StyleSheet, useWindowDimensions, Platform, Linking } from 'react-native';
import { Input } from '@/components/Input';
import { Button } from '@/components/Button';
import { ErrorMessage } from '@/components/ErrorMessage';
import { Logo } from '@/components/Logo';
import { TextLink } from '@/components/TextLink';
import { colors } from '@/styles/colors';
import { handleTwoFactorLogin, resendTwoFactorCode } from '@/services/authService';
import { validate2FACode } from '@/services/validationService';
import { ERROR_MESSAGES } from '@/utils/constants';

interface TwoFactorFormProps {
  onBack: () => void;
  email: string;
  password: string;
  nextUrl?: string;
}

export const TwoFactorForm: React.FC<TwoFactorFormProps> = ({ onBack, email, password, nextUrl }) => {
  const { width } = useWindowDimensions();
  const isDesktop = width >= 768;

  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  const handleSubmit = async () => {
    setError(null);

    // Validate code
    const codeValidation = validate2FACode(code);
    if (codeValidation) {
      setError(codeValidation);
      return;
    }

    setLoading(true);

    try {
      await handleTwoFactorLogin(email, password, code, nextUrl);

      // Login successful — redirect
      if (Platform.OS === 'web') {
        window.location.href = '/app/';
      } else {
        Linking.openURL('/app/');
      }
    } catch (err: any) {
      setError(ERROR_MESSAGES.INVALID_2FA_CODE);
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setResending(true);
    setError(null);

    try {
      await resendTwoFactorCode({ email, password });
      setResent(true);
    } catch (err: any) {
      setError('Failed to resend code. Please try again.');
    } finally {
      setResending(false);
    }
  };

  return (
    <View style={styles.formContainer}>
      <Logo />
      
      <View style={styles.formContent}>
        <Text style={styles.subheading}>Two Factor Authentication</Text>

        <View style={styles.topNote}>
          {resent ? (
            <Text style={styles.resentText}>Code resent successfully.</Text>
          ) : (
            <>
              <Text style={styles.topNoteText}>Didn't get a code? </Text>
              <TextLink href="#" onPress={handleResendCode}>
                {resending ? 'Sending...' : 'Resend code.'}
              </TextLink>
            </>
          )}
        </View>

        {error && <ErrorMessage message={error} />}

        <Input
          label="Code sent to mobile device"
          value={code}
          onChangeText={setCode}
          placeholder="Enter code"
          maxLength={254}
          autoComplete="one-time-code"
          onSubmitEditing={handleSubmit}
        />

        <View style={styles.formFooter}>
          <View style={isDesktop && styles.buttonContainer}>
            <Button
              title="Submit"
              onPress={handleSubmit}
              loading={loading}
            />
          </View>

          <View style={[isDesktop && styles.buttonContainer, styles.backLinkContainer]}>
            <TextLink href="#" onPress={onBack}>
              Back
            </TextLink>
          </View>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  formContainer: {
    width: '100%',
  },
  formContent: {
    width: '100%',
  },
  subheading: {
    fontSize: 20,
    lineHeight: 28,
    color: colors.textPrimary,
    marginBottom: 24,
    fontWeight: '600',
  },
  topNote: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 24,
  },
  topNoteText: {
    fontSize: 14,
    color: colors.textSecondary,
  },
  resentText: {
    fontSize: 14,
    color: colors.success,
  },
  formFooter: {
    marginTop: 24,
    gap: 16,
  },
  backLinkContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 8,
  },
  buttonContainer: {
    alignItems: 'flex-start',
  },
});
